import React, { FC, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Avatar } from 'components/Avatar';
import { Footer } from 'components/Footer';
import { ReactComponent as SuccessIcon } from 'assets/images/success.svg';
import { Loader } from 'components/Loader';
import { ESearchParams, EYandexMetrikaEvent } from 'common/const';
import { getUserName } from 'common/helpers/user.helper';
import { useSearchParamsHook } from 'common/hooks/useSearchParamsHook';
import { useService } from 'common/hooks/useService';
import { parsePaymentId, takeCheckoutForCompletion } from 'common/helpers/metrika.helper';
import { sendYandexMetrikaEvent } from 'common/helpers/yandex.helper';
import { ITransactionData } from 'common/types/transaction.types';

export const SuccessLayout: FC = () => {
  const [transaction, setTransaction] = useState<ITransactionData | null>(null);
  const { t } = useTranslation();
  const { getSearchParam } = useSearchParamsHook();
  const { Service, loading } = useService();

  const userName = getUserName(transaction?.user.firstname, transaction?.user.lastname);
  const transactionId = getSearchParam(ESearchParams.TransactionId);

  const reviewSubscription = transaction?.organization.review_subscription;
  const reviewLink = transaction?.organization.reviews_link;
  const organizationName = transaction?.organization.name;

  const getTransaction = async (id: string) => {
    const response = await Service.getTransactionById(id);

    if (response) {
      setTransaction(response);
      sendPaymentCompleted(id, response);
    }
  };

  /* Завершение воронки по снимку начатой на устройстве попытки этого же платежа (номер из ответа о платеже)
   *  не старше 60 минут, один раз на платёж с устройства (metrika-landing, R-4, ADR-018). Нет номера в ответе
   *  (старый бэк), нет снимка этого платежа или платёж уже отмечен - события нет, снимок не трогается. */
  const sendPaymentCompleted = (tuid: string, data: ITransactionData) => {
    const paymentId = parsePaymentId(data.payment_id);
    const snapshot = paymentId !== null ? takeCheckoutForCompletion(tuid, paymentId, Date.now()) : null;

    if (snapshot) {
      sendYandexMetrikaEvent(EYandexMetrikaEvent.PaymentCompleted, {
        org_id: snapshot.org_id,
        source: snapshot.source,
        flow_type: snapshot.flow_type,
        checkout_version: snapshot.checkout_version,
        payment_id: paymentId,
        payment_method: snapshot.payment_method,
        currency: snapshot.currency,
        guest_covers_commission: snapshot.guest_covers_commission,
      });
    }
  };

  const onLeaveYourReviewClick = () => {
    if (reviewLink) {
      window.open(reviewLink, '_blank', 'noopener,noreferrer');
    }
  };

  useEffect(() => {
    if (transactionId) {
      getTransaction(transactionId);
    }
  }, [transactionId]);

  if (loading) {
    return <Loader />;
  }

  return (
    <div className="success-layout">
      <div className="success-layout__user">
        <Avatar url={transaction?.user.avatar} className="success-layout__user-avatar" />

        <div className="success-layout__user-username">{userName}</div>

        {organizationName && <div className="success-layout__user-company">{organizationName}</div>}
      </div>

      <div className="success-layout__message">
        <SuccessIcon />

        <div>{t('success.thanks')}</div>
      </div>

      <div className="success-layout__description">{t('success.description')}</div>

      {reviewSubscription && !!reviewLink?.length && (
        <div className="btn btn-submit">
          <button onClick={onLeaveYourReviewClick}>{t('success.leaveYourReview')}</button>
        </div>
      )}

      <Footer />
    </div>
  );
};
